import { Field } from 'api/models/Form';
import { FieldTemplate, Patient, Visit } from 'api/models';
import { FieldType } from 'api/models/FieldTemplate';
import * as yup from 'yup';
import moment from 'moment';
import i18next from 'i18next';
import { DEFAULT_VALUE_OPTION_VISIT_DATE } from 'components/GlobalDialog/AddFieldTemplate/utils';

function getNumberValidationSchema(template: FieldTemplate) {
  const { minValue, maxValue, isRequired } = template.fieldAttributes ?? {};
  return yup
    .mixed()
    .test('is-valid', 'general.errors.invalid', (value, { createError }) => {
      if (isRequired && !value) {
        return createError(
          new yup.ValidationError(i18next.t('forms.errors.isRequired')),
        );
      }

      const number = value as number;

      const numberExists = number !== undefined && number !== null;

      if (minValue && numberExists && number < minValue) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.minValue', { minValue }),
          ),
        );
      }

      if (maxValue && numberExists && number > maxValue) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.maxValue', { maxValue }),
          ),
        );
      }

      return true;
    })
    .nullable();
}

function getTextValidationSchema(template: FieldTemplate) {
  const { minLength, maxLength, isRequired } = template.fieldAttributes ?? {};
  return yup
    .mixed()
    .test('is-valid', 'general.errors.invalid', (value, { createError }) => {
      if (isRequired && !value) {
        return createError(
          new yup.ValidationError(i18next.t('forms.errors.isRequired')),
        );
      }

      const str = value as string;

      if (minLength && str && str.length < minLength) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.minLength', { minLength }),
          ),
        );
      }

      if (maxLength && str && str.length > maxLength) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.maxLength', { maxLength }),
          ),
        );
      }

      return true;
    })
    .nullable();
}

function getSelectValidationSchema(template: FieldTemplate) {
  const { isRequired } = template.fieldAttributes ?? {};
  return yup
    .mixed()
    .test('is-valid', 'general.errors.invalid', (value, { createError }) => {
      const val = value as string[] | null | string;
      if (isRequired && (!val || val?.length === 0)) {
        return createError(
          new yup.ValidationError(i18next.t('forms.errors.isRequired')),
        );
      }

      return true;
    })
    .nullable();
}

function getDateValidationSchema(
  template: FieldTemplate,
  patient?: Patient,
  visit?: Visit,
) {
  const { isRequired, cantBeEarlierThanBirthDate, cantBeLaterThanVisitDate } =
    template.fieldAttributes ?? {};
  return yup
    .mixed()
    .test('is-valid', 'general.errors.invalid', (value, { createError }) => {
      if (isRequired && !value) {
        return createError(
          new yup.ValidationError(i18next.t('forms.errors.isRequired')),
        );
      }

      const visitDate = moment(visit?.createdAt);
      const momentDate = moment(value ?? '');

      if (
        cantBeEarlierThanBirthDate &&
        patient?.birthDate &&
        momentDate.isBefore(patient.birthDate, 'day')
      ) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.cantBeEarlierThanBirthDate'),
          ),
        );
      }
      if (cantBeLaterThanVisitDate && momentDate.isAfter(visitDate)) {
        return createError(
          new yup.ValidationError(
            i18next.t('forms.errors.cantBeLaterThanVisitDate'),
          ),
        );
      }

      return true;
    })
    .nullable();
}

interface getFormValidationSchemaProps {
  templates: FieldTemplate[];
  patient?: Patient;
  visit?: Visit;
}

export function getFormValidationSchema({
  templates,
  patient,
  visit,
}: getFormValidationSchemaProps) {
  const schema: {
    [key: string]:
      | yup.StringSchema
      | yup.NumberSchema
      | yup.DateSchema
      | yup.MixedSchema<
          yup.AnyObject | null | undefined,
          yup.AnyObject,
          undefined,
          ''
        >;
  } = {};
  templates.forEach(template => {
    if (template.fieldType === FieldType.Number) {
      schema[template.id] = getNumberValidationSchema(template);
    }

    if (template.fieldType === FieldType.Text) {
      schema[template.id] = getTextValidationSchema(template);
    }

    if (template.fieldType === FieldType.Select) {
      schema[template.id] = getSelectValidationSchema(template);
    }

    if (template.fieldType === FieldType.Date) {
      schema[template.id] = getDateValidationSchema(template, patient, visit);
    }
  });

  return yup.object(schema);
}

export function getInitialFormValues(
  templates: FieldTemplate[],
  fields: Field[] = [],
  visit?: Visit,
) {
  const initialValues: {
    [key: string]: string | number | string[] | null | boolean | undefined;
  } = {};

  fields.forEach(field => {
    const template = templates.find(t => t.id === field.fieldTemplateId)! ?? {};
    let templateId = template.id;

    if ((field.repeatIndex ?? -1) >= 1) {
      templateId = `${template.id}:${field.repeatIndex}`;
    }

    if (field.layoutRepeatIndex !== undefined) {
      templateId = `${template.id}|${field.layoutRepeatIndex}`;
    }

    if (
      field.layoutRepeatIndex !== undefined &&
      (field.repeatIndex ?? -1) >= 1
    ) {
      templateId = `${template.id}:${field.repeatIndex}|${field.layoutRepeatIndex}`;
    }

    if (field?.value) {
      initialValues[templateId] = field.value;
    } else {
      if (field?.value === null) {
        initialValues[templateId] = null;
      } else {
        if (
          template.fieldAttributes.defaultValueType ===
          DEFAULT_VALUE_OPTION_VISIT_DATE
        ) {
          // @ts-ignore
          initialValues[templateId] = new Date(visit?.date);
        } else {
          initialValues[templateId] = template.fieldAttributes.defaultValue;
        }
      }
    }
  });
  return initialValues;
}
